import { getCurrentUserInfoUsersMeGet, loginUserLoginPost, logoutUserUsersLogoutPost } from '@/client/sdk.gen';
import type { UserDetailResponse } from '@/client/types.gen';
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

interface AuthContextType {
  user: UserDetailResponse;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // check if user is logged inm
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = (await getCurrentUserInfoUsersMeGet());
        if (userData.response.status !== 200) {
          throw new Error('Not authenticated');
        }
        setUser(userData.response.body);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      const response = await loginUserLoginPost({ body: { username, password } });
      if (response.response.status !== 200) {
        throw new Error('Login failed');
      }
      const userData = (await getCurrentUserInfoUsersMeGet());
      if (userData.response.status !== 200) {
        throw new Error('Not authenticated');
      }
      setUser(userData.response.body);
    }
    catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    await logoutUserUsersLogoutPost();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}
